import React, { useRef, useEffect, useCallback } from "react";
import { Box, makeStyles, useTheme } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative"
  },
  canvas: {
    width: "100%",
    height: "100%",
    borderRadius: theme.shape.borderRadius
  },
  staticBars: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    height: "100%",
    width: "100%"
  },
  bar: {
    width: 3,
    backgroundColor: theme.palette.grey[400],
    borderRadius: 2,
    transition: "all 0.3s ease"
  }
}));

const WaveformVisualization = ({
  audioLevel = 0,
  isRecording = false,
  isSpeaking = false,
  isProcessing = false,
  audioData = null,
  staticBars = 20
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  // Generate random heights for static visualization
  const generateStaticHeights = useCallback((count, baseLevel = 0.1) => {
    return Array.from({ length: count }, () => 
      Math.max(baseLevel, Math.random() * (0.3 + audioLevel * 0.7))
    );
  }, [audioLevel]);

  // Draw waveform visualization
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = canvas;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Set canvas size to match display size
    const rect = canvas.getBoundingClientRect();
    if (!rect) return;
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const displayWidth = rect.width;
    const displayHeight = rect.height;

    // Configuration
    const barCount = 32;
    const barWidth = Math.max(2, Math.floor(displayWidth / barCount) - 2);
    const barSpacing = 2;
    const centerY = displayHeight / 2;

    // Get colors based on state
    let barColor = theme.palette.grey[400];
    if (isRecording) {
      barColor = theme.palette.error.main;
    } else if (isSpeaking) {
      barColor = theme.palette.success.main;
    } else if (isProcessing) {
      barColor = theme.palette.warning.main;
    } else if (audioLevel > 0) {
      barColor = theme.palette.primary.main;
    }

    ctx.fillStyle = barColor;

    // Draw bars
    for (let i = 0; i < barCount; i++) {
      const x = i * (barWidth + barSpacing);
      
      let barHeight;
      if (audioData && audioData.length > 0) {
        // Use actual audio data
        const dataIndex = Math.floor((i / barCount) * audioData.length);
        barHeight = Math.max(4, (audioData[dataIndex] / 255) * displayHeight * 0.8);
      } else if (isRecording || isSpeaking) {
        // Animate based on audio level with some randomness
        const baseHeight = audioLevel * displayHeight * 0.6;
        const randomFactor = 0.3 + Math.random() * 0.7;
        barHeight = Math.max(4, baseHeight * randomFactor);
      } else if (isProcessing) {
        // Processing animation
        const time = Date.now() * 0.005;
        const wave = Math.sin(time + i * 0.5) * 0.5 + 0.5;
        barHeight = Math.max(4, wave * displayHeight * 0.4);
      } else {
        // Static visualization
        barHeight = 4 + Math.random() * 8;
      }

      const y = centerY - barHeight / 2;
      
      // Draw bar with rounded corners
      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
      ctx.fill();
    }
  }, [theme, audioLevel, audioData, isRecording, isSpeaking, isProcessing]);

  // Animation loop
  const animate = useCallback(() => {
    drawWaveform();
    animationRef.current = requestAnimationFrame(animate);
  }, [drawWaveform]);

  // Start/stop animation based on state
  useEffect(() => {
    if (isRecording || isSpeaking || isProcessing || audioLevel > 0) {
      if (!animationRef.current) {
        animate();
      }
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Draw static state
      drawWaveform();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [isRecording, isSpeaking, isProcessing, audioLevel, animate, drawWaveform]);

  // Handle canvas resize
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current) {
        drawWaveform();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [drawWaveform]);

  // Fallback to CSS bars if canvas is not supported
  if (!HTMLCanvasElement.prototype.roundRect) {
    // Polyfill for roundRect if not supported
    HTMLCanvasElement.prototype.roundRect = function(x, y, width, height, radius) {
      const ctx = this.getContext("2d");
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    };
  }

  return (
    <Box className={classes.root}>
      <canvas
        ref={canvasRef}
        className={classes.canvas}
        style={{
          filter: isRecording ? "drop-shadow(0 0 8px rgba(244, 67, 54, 0.4))" : 
                  isSpeaking ? "drop-shadow(0 0 8px rgba(76, 175, 80, 0.4))" :
                  isProcessing ? "drop-shadow(0 0 8px rgba(255, 152, 0, 0.4))" : "none"
        }}
      />
    </Box>
  );
};

export default WaveformVisualization;